import { localizedText } from '@/utils/i18n'
import { getStoredConsent, setConsent } from '@/utils/analytics'

/**
 * Cookie/analytics consent banner. Shown only until the visitor makes a
 * choice (stored in localStorage). Accepting enables GA4 via Consent Mode v2;
 * declining keeps analytics off. Mounted on document.body by main.ts.
 */
export function mountConsentBanner(): void {
  // Remove any stale banner baked into a prerendered snapshot (its event
  // handlers were not serialized, so it must be rebuilt by live JS).
  document.getElementById('consent-banner')?.remove()

  if (getStoredConsent() !== null) return // already chose

  const banner = document.createElement('div')
  banner.id = 'consent-banner'
  banner.setAttribute('role', 'dialog')
  banner.setAttribute('aria-label', localizedText({ en: 'Cookie consent', ko: '쿠키 동의' }))
  banner.className =
    'fixed bottom-0 inset-x-0 z-[100] bg-slate-900/95 backdrop-blur-md text-white border-t border-white/10 px-4 py-4 sm:px-6'

  const inner = document.createElement('div')
  inner.className =
    'mx-auto max-w-5xl flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6'

  // Message
  const text = document.createElement('p')
  text.className = 'text-sm text-slate-200 leading-relaxed flex-1'
  text.append(
    localizedText({
      en: 'We use analytics cookies to understand how visitors use our site. They are set only with your consent. ',
      ko: '방문자가 사이트를 어떻게 이용하는지 파악하기 위해 분석 쿠키를 사용합니다. 동의하신 경우에만 설정됩니다. ',
    })
  )
  const policyLink = document.createElement('a')
  policyLink.href = '/privacy'
  policyLink.className = 'underline text-orange-400 hover:text-orange-300 whitespace-nowrap'
  policyLink.textContent = localizedText({ en: 'Learn more', ko: '자세히 보기' })
  policyLink.addEventListener('click', (e) => {
    e.preventDefault()
    window.dispatchEvent(new CustomEvent('navigate', { detail: '/privacy' }))
  })
  text.append(policyLink)

  // Buttons
  const actions = document.createElement('div')
  actions.className = 'flex items-center gap-3 shrink-0'

  const decline = document.createElement('button')
  decline.type = 'button'
  decline.className =
    'px-5 py-2 rounded-full text-sm font-semibold border border-white/25 text-white/90 hover:bg-white/10 transition-colors'
  decline.textContent = localizedText({ en: 'Decline', ko: '거부' })

  const accept = document.createElement('button')
  accept.type = 'button'
  accept.className =
    'px-5 py-2 rounded-full text-sm font-bold bg-orange-500 text-white hover:bg-orange-600 transition-colors'
  accept.textContent = localizedText({ en: 'Accept', ko: '동의' })

  const dismiss = () => banner.remove()
  decline.addEventListener('click', () => {
    setConsent('denied')
    dismiss()
  })
  accept.addEventListener('click', () => {
    setConsent('granted')
    dismiss()
  })

  actions.appendChild(decline)
  actions.appendChild(accept)

  inner.appendChild(text)
  inner.appendChild(actions)
  banner.appendChild(inner)
  document.body.appendChild(banner)
}
